import {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from "react";
import {
    Dimensions,
    KeyboardAvoidingView,
    Modal,
    Platform,
    Pressable,
    View,
    type LayoutChangeEvent,
    type StyleProp,
    type ViewStyle,
} from "react-native";
import Animated, {
    ReduceMotion,
    useAnimatedStyle,
    useSharedValue,
    withTiming,
} from "react-native-reanimated";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { useKeyboardHeight } from "@/hooks/useKeyboardHeight";
import { cn } from "@/lib/utils";

type SheetModalLayoutValue = {
    /** Max height available for sheet children (excludes bottom safe-area / keyboard spacer). */
    bodyMaxHeight?: number;
};

const SheetModalLayoutContext = createContext<SheetModalLayoutValue>({});

/** Use inside SheetModal to constrain a ScrollView when `maxHeightRatio` is set. */
export function useSheetModalBodyMaxHeight(): number | undefined {
    return useContext(SheetModalLayoutContext).bodyMaxHeight;
}

type SheetModalProps = {
    visible: boolean;
    onRequestClose: () => void;
    children: ReactNode;
    /** Extra classes for the sheet / dialog panel. */
    contentClassName?: string;
    /**
     * Cap sheet height as a fraction of the window (e.g. 0.7).
     * Prefer this over `max-h-[70%]` — percentages are unreliable inside Android Modal.
     * Pair with `useSheetModalBodyMaxHeight()` on an inner ScrollView.
     */
    maxHeightRatio?: number;
    /** When false, backdrop and back button will not dismiss. */
    dismissible?: boolean;
    accessibilityLabel: string;
    /** Fires after the native modal is on screen. */
    onShow?: () => void;
};

const isWeb = Platform.OS === "web";
const isAndroid = Platform.OS === "android";
const SHEET_SLIDE_MS = 280;

/**
 * Slide up after the sheet has a real height. `SlideInDown` (entering) reads
 * layout on the first Modal frame, which is often wrong on iPad — leftover
 * translateY parks the sheet under the visible bottom.
 */
function SlideUpSheet({
    className,
    style,
    children,
}: {
    className?: string;
    style?: StyleProp<ViewStyle>;
    children: ReactNode;
}) {
    const translateY = useSharedValue(Dimensions.get("screen").height);
    const hasStarted = useRef(false);

    const animatedStyle = useAnimatedStyle(() => ({
        transform: [{ translateY: translateY.value }],
    }));

    function handleLayout(event: LayoutChangeEvent) {
        const height = event.nativeEvent.layout.height;
        if (hasStarted.current || height < 1) {
            return;
        }
        hasStarted.current = true;
        translateY.set(height);
        translateY.set(
            withTiming(0, {
                duration: SHEET_SLIDE_MS,
                reduceMotion: ReduceMotion.System,
            }),
        );
    }

    return (
        <Animated.View
            className={className}
            style={[style, animatedStyle]}
            onLayout={handleLayout}
        >
            {children}
        </Animated.View>
    );
}

/**
 * Native: bottom sheet sliding up from the bottom.
 * Web: classic centered dialog modal.
 *
 * iOS: KeyboardAvoidingView lifts the sheet; card-color fill covers the gap
 * above the rounded keyboard.
 * Android: in-flow card-color spacer; edge-to-edge Modal so the sheet sits on
 * the real bottom (avoid a nav-bar-sized gap that only clears after keyboard).
 */
export function SheetModal({
    visible,
    onRequestClose,
    children,
    contentClassName,
    maxHeightRatio,
    dismissible = true,
    accessibilityLabel,
}: SheetModalProps) {
    const insets = useSafeAreaInsets();
    const { keyboardHeight } = useKeyboardHeight(isAndroid && visible);
    const [iosPresented, setIosPresented] = useState(false);
    const [iosCanDismiss, setIosCanDismiss] = useState(false);

    if (!visible && (iosPresented || iosCanDismiss)) {
        setIosPresented(false);
        setIosCanDismiss(false);
    }

    useEffect(() => {
        if (!visible || Platform.OS !== "ios") {
            return;
        }

        // iOS RCTModalHostView often skips presentation when `visible` is true
        // on the same commit as mount. Present on the next frame after `visible`.
        const frameId = requestAnimationFrame(() => {
            setIosPresented(true);
        });
        // The opening tap can land on the newly mounted backdrop and dismiss
        // the sheet immediately. Ignore dismiss until that gesture is over.
        const dismissTimer = setTimeout(() => {
            setIosCanDismiss(true);
        }, 400);

        return () => {
            cancelAnimationFrame(frameId);
            clearTimeout(dismissTimer);
        };
    }, [visible]);

    const isPresented = Platform.OS === "ios" ? iosPresented : true;
    const canDismiss = Platform.OS === "ios" ? iosCanDismiss : true;

    const bottomPad = keyboardHeight > 0 ? 0 : insets.bottom;
    const sheetMaxHeight =
        maxHeightRatio != null
            ? Dimensions.get("window").height * maxHeightRatio
            : undefined;
    const bodyMaxHeight =
        sheetMaxHeight != null
            ? Math.max(sheetMaxHeight - bottomPad, 0)
            : undefined;

    const layoutValue = useMemo(() => ({ bodyMaxHeight }), [bodyMaxHeight]);

    const sheetStyle: StyleProp<ViewStyle> =
        sheetMaxHeight != null
            ? {
                  maxHeight: sheetMaxHeight,
                  width: "100%",
                  overflow: "hidden",
              }
            : Platform.OS === "ios"
              ? { overflow: "visible" }
              : undefined;

    function handleClose() {
        if (dismissible && canDismiss) {
            onRequestClose();
        }
    }

    const sheetBody = (
        <SheetModalLayoutContext.Provider value={layoutValue}>
            {children}
            {/*
              Pad for home indicator / gesture bar. On Android the Modal is
              edge-to-edge (navigationBarTranslucent), so we do need this —
              unlike the previous double-inset bug with a non-translucent Modal.
            */}
            <View style={{ height: bottomPad }} />
            {Platform.OS === "ios" ? (
                /*
                  KeyboardAvoidingView padding is transparent, so the dimmed
                  backdrop shows between the flat sheet bottom and the iOS
                  keyboard's rounded top. Extend card color below the sheet.
                */
                <View
                    pointerEvents="none"
                    className="absolute right-0 left-0 bg-card"
                    style={{ top: "100%", height: 400 }}
                />
            ) : null}
        </SheetModalLayoutContext.Provider>
    );

    const sheet = isWeb ? (
        <View
            className={cn(
                "z-10 w-full max-w-lg overflow-hidden rounded-lg border border-border bg-card shadow-lg shadow-black/5",
                contentClassName,
            )}
            style={
                sheetMaxHeight != null
                    ? { maxHeight: sheetMaxHeight }
                    : undefined
            }
        >
            <SheetModalLayoutContext.Provider value={layoutValue}>
                {children}
            </SheetModalLayoutContext.Provider>
        </View>
    ) : isAndroid ? (
        // No slide-in on Android: Reanimated transforms inside Modal can
        // finish with a stale translateY (sheet floating above the bottom)
        // until a keyboard layout pass clears it.
        <View
            className={cn("rounded-t-sheet bg-card", contentClassName)}
            style={sheetStyle}
        >
            {sheetBody}
        </View>
    ) : (
        <SlideUpSheet
            className={cn("rounded-t-sheet bg-card", contentClassName)}
            style={sheetStyle}
        >
            {sheetBody}
        </SlideUpSheet>
    );

    const sheetWithKeyboardSpacer = (
        <View>
            {sheet}
            {isAndroid && keyboardHeight > 0 ? (
                <View className="bg-card" style={{ height: keyboardHeight }} />
            ) : null}
        </View>
    );

    // Unmount when closed. Keeping a transparent Modal at visible={false}
    // (especially with Reanimated entering views) can leave a full-screen
    // ghost layer that swallows all touches until the app is reloaded.
    if (!visible) {
        return null;
    }

    return (
        <Modal
            visible={isPresented}
            transparent
            animationType={isWeb ? "fade" : "none"}
            onRequestClose={handleClose}
            statusBarTranslucent={isAndroid}
            navigationBarTranslucent={isAndroid}
            presentationStyle="overFullScreen"
            supportedOrientations={
                Platform.OS === "ios" && Platform.isPad
                    ? [
                          "portrait",
                          "portrait-upside-down",
                          "landscape",
                          "landscape-left",
                          "landscape-right",
                      ]
                    : ["portrait"]
            }
        >
            {isWeb ? (
                <View className="flex-1 items-center justify-center p-4">
                    <Pressable
                        accessibilityRole="button"
                        accessibilityLabel={accessibilityLabel}
                        className="absolute inset-0 bg-black/40"
                        onPress={handleClose}
                    />
                    {sheet}
                </View>
            ) : Platform.OS === "ios" ? (
                <KeyboardAvoidingView
                    behavior="padding"
                    className="flex-1 justify-end"
                    style={{ flex: 1 }}
                >
                    <Pressable
                        accessibilityRole="button"
                        accessibilityLabel={accessibilityLabel}
                        pointerEvents={canDismiss ? "auto" : "none"}
                        className="absolute inset-0 bg-black/40"
                        onPress={handleClose}
                    />
                    {sheet}
                </KeyboardAvoidingView>
            ) : (
                <View className="flex-1 justify-end" style={{ flex: 1 }}>
                    <Pressable
                        accessibilityRole="button"
                        accessibilityLabel={accessibilityLabel}
                        className="absolute inset-0 bg-black/40"
                        onPress={handleClose}
                    />
                    {sheetWithKeyboardSpacer}
                </View>
            )}
        </Modal>
    );
}
